import { useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalArbitrage from "~/api/hooks/useBatteryProposalArbitrage"
import { useDoesCurrentUserHavePermissions } from "~/api/hooks/useCurrentUser"
import { GreenbuttonDataDrawer } from "~/containers/Proposal/Setup/GreenbuttonDataDrawer"

import { personName } from "../person-display"
import { BatterySavingsInputControl } from "./BatterySavingsInputControl"

/**
 * Greenbutton Data for a Battery-Only Proposal. It is the Solar drawer with the
 * Battery route palette — same tabs, same controls, same connection flow — so
 * a seller who has used it once already knows this one.
 */
export const GreenbuttonStep = ({
  onClose,
  proposalId,
}: {
  onClose: () => void
  proposalId: string
}): JSX.Element | null => {
  const { data: proposal, refetch } = useBatteryProposal(proposalId)
  const arbitrageQuery = useBatteryProposalArbitrage(proposalId)
  const canUseSupportDiagnostics = useDoesCurrentUserHavePermissions([
    `vision_admin`,
  ])
  const queryClient = useQueryClient()

  // Applying usage changes consumption, and Battery savings are calculated
  // from it, so the saved arbitrage result has to be re-read too.
  const handleUsageApplied = useCallback(async () => {
    const result = await refetch()
    await queryClient.invalidateQueries(
      queryKeys.batteryProposal.arbitrage(proposalId),
    )

    if (result.isError) {
      throw result.error ?? new Error(`Battery proposal usage could not refresh`)
    }
  }, [proposalId, queryClient, refetch])

  if (!proposal) {
    return null
  }

  return (
    <GreenbuttonDataDrawer
      canUseSupportDiagnostics={canUseSupportDiagnostics}
      forBatteryOnlyProposal
      homeownerEmail={proposal.homeowner?.email ?? ``}
      homeownerName={
        proposal.homeowner
          ? personName(proposal.homeowner, `the homeowner`)
          : `the homeowner`
      }
      onClose={onClose}
      onUsageApplied={handleUsageApplied}
      afterCapturedUsage={
        <BatterySavingsInputControl
          proposalId={proposalId}
          savingsInput={arbitrageQuery.data?.savingsInput}
        />
      }
      proposalId={proposalId}
      siteId={proposal.siteId}
      theme="dark"
      utilityCompanyName={proposal.tariff?.utilityCompanyName ?? ``}
    />
  )
}
